/*
TRANE CONFIDENTIAL

2021 – 2022 Copyright Trane U.S. Inc.
All Rights Reserved.

NOTICE: All information contained herein is, and remains the property of
Trane U.S. Inc. The source code, object code, intellectual and technical
concepts contained herein are proprietary to Trane U.S. Inc. and may be
covered by patents and patent applications and are protected by trade secret
and copyright law. Dissemination of this information or reproduction of this
material is strictly forbidden unless prior written permission is obtained
from Trane U.S. Inc. Do not use this software without the prior written
permission of Trane U.S. Inc.
*/

/* @flow */
import * as React from "react";
import {
  IconBatteryFull,
  IconBatteryMostlyFull,
  IconBatteryLow,
  IconBatteryEmpty,
  IconLightbulb0,
  IconLightbulb100,
  IconBlindsClosed,
  IconBlindsMostlyClosed,
  IconBlindsHalfOpen,
  IconBlindsMostlyOpen,
  IconBlindsOpen,
  IconVolumeHigh,
  IconVolumeMedium,
  IconVolumeLow,
  IconVolumeMute,
  IconWand,
  IconWaterDrop,
  IconWaterDropOff,
  IconRachioLogo,
  IconUnlocked,
  IconLocked,
  IconThermostat,
  IconSnowflake,
  IconFire,
  IconDroplets,
  IconLightSwitchOff,
  IconLightSwitchOn,
  IconCameraDisconnected,
  IconCamera,
  IconWifi,
  IconSmokeDetectorTriggered,
  IconSmokeDetector,
  IconCheck,
  IconWarning,
  IconRefresh,
  IconLightning,
  IconEqualizer,
  IconPlay,
  IconPause,
  IconPencil,
  IconRemove,
} from "nx-components/Icons";
import parseIconsFromServer from "nexia_mobile/parseIconsFromServer";
import type { IconList, PercentInteger } from "nexia_mobile/api/types";

const Icons = {
  getString(icon: ?IconList): string {
    if (icon == null) {
      return "";
    }

    const icons = parseIconsFromServer(icon);
    if (icons.length === 0) {
      return "";
    }

    const { name, modifiers } = icons[0];
    if (modifiers.length > 0) {
      return `${name} ${modifiers.join(" ")}`;
    } else {
      return name;
    }
  },

  getIconModifiers(icon: ?IconList): Array<string> {
    if (icon == null) {
      return [];
    }

    const icons = parseIconsFromServer(icon);
    if (icons.length === 0) {
      return [];
    }

    return icons[0].modifiers;
  },

  getBatteryLevelIcon(level: PercentInteger): React.Element<any> {
    if (level > 75) {
      return <IconBatteryFull />;
    }
    if (level > 30) {
      return <IconBatteryMostlyFull />;
    }
    if (level > 1) {
      return <IconBatteryLow modifiers={["alert"]} />;
    }
    return <IconBatteryEmpty />;
  },

  getLightLevelIcon(level: PercentInteger): React.Element<any> {
    if (level === 0) {
      return <IconLightbulb0 />;
    }
    return <IconLightbulb100 />;
  },

  getWindowCoveringLevelIcon(percentage: PercentInteger): React.Element<any> {
    if (percentage >= 97) {
      return <IconBlindsClosed />;
    }
    if (percentage >= 67) {
      return <IconBlindsMostlyClosed />;
    }
    if (percentage >= 34) {
      return <IconBlindsHalfOpen />;
    }
    if (percentage >= 4) {
      return <IconBlindsMostlyOpen />;
    }
    return <IconBlindsOpen />;
  },

  getVolumeIcon(volume: PercentInteger): React.Element<any> {
    if (volume > 75) {
      return <IconVolumeHigh />;
    }
    if (volume > 30) {
      return <IconVolumeMedium />;
    }
    if (volume > 0) {
      return <IconVolumeLow />;
    }
    return <IconVolumeMute />;
  },

  getEventDescriptionIcon(initialDescription: ?string): ?React.Element<any> {
    if (typeof initialDescription !== "string") {
      return null;
    }
    let description = initialDescription.toLowerCase();
    if (description.match(/battery/)) {
      let matches = description.match(/battery level: (\d+)%/);
      if (matches) {
        return this.getBatteryLevelIcon(parseInt(matches[1]));
      }
      return <IconBatteryFull />;
    }

    if (description.match(/automation/)) {
      return <IconWand />;
    }

    if (description.match(/rachio/)) {
      if (description.match(/^water(ed|ing)|started manual schedule/)) {
        return <IconWaterDrop />;
      }
      if (description.match(/^stopped manual schedule/)) {
        return <IconWaterDropOff />;
      }
      return <IconRachioLogo />;
    }

    if (description.match(/unlocked/)) {
      return <IconUnlocked />;
    }
    if (description.match(/locked/) || description.match(/activated/)) {
      return <IconLocked />;
    }

    if (description.match(/new temperature/)) {
      return <IconThermostat />;
    }
    if (description.match(/cool setpoint/)) {
      return <IconSnowflake />;
    }
    if (description.match(/heat setpoint/)) {
      return <IconFire />;
    }
    if (description.match(/humidity/)) {
      return <IconDroplets />;
    }

    if (description.match(/turned off/)) {
      return <IconLightSwitchOff />;
    }
    if (description.match(/turned on/)) {
      return <IconLightSwitchOn />;
    }

    if (description.match(/camera disconnected/)) {
      return <IconCameraDisconnected />;
    }
    if (description.match(/camera connected/)) {
      return <IconCamera />;
    }

    if (description.match(/signal report/)) {
      return <IconWifi />;
    }

    if (description.match(/smoke detected/)) {
      return <IconSmokeDetectorTriggered />;
    }
    if (description.match(/smoke cleared/)) {
      return <IconSmokeDetector />;
    }

    if (description.match(/no .* detected/)) {
      return <IconCheck />;
    }
    if (description.match(/tamper|alarm|motion|water/)) {
      return <IconWarning />;
    }

    if (description.match(/reset/)) {
      return <IconRefresh />;
    }
    if (description.match(/triggered/)) {
      return <IconLightning />;
    }

    if (description.match(/changed.*mode/)) {
      return <IconEqualizer />;
    }

    if (description.match(/set to play/)) {
      return <IconPlay />;
    }
    if (description.match(/set to pause/)) {
      return <IconPause />;
    }

    let volumeMatches = description.match(/volume set to (\d+)%/);
    if (volumeMatches) {
      return this.getVolumeIcon(parseInt(volumeMatches[1]));
    }

    let windowCoveringClosedMatches = description.match(/set to (\d+)% closed/);
    if (windowCoveringClosedMatches) {
      return this.getWindowCoveringLevelIcon(
        parseInt(windowCoveringClosedMatches[1])
      );
    }

    let windowCoveringOpenMatches = description.match(/set to (\d+)% open/);
    if (windowCoveringOpenMatches) {
      return this.getWindowCoveringLevelIcon(
        100 - parseInt(windowCoveringOpenMatches[1])
      );
    }

    let lightMatches = description.match(/set to (\d+)%/);
    if (lightMatches) {
      return this.getLightLevelIcon(parseInt(lightMatches[1]));
    }

    if (description.match(/editcontact/)) {
      return <IconPencil />;
    }
    if (description.match(/deletecontact/)) {
      return <IconRemove style={{ color: "E2231A" }} />;
    }

    return <IconCheck />;
  },
};

export default Icons;



// WEBPACK FOOTER //
// ./src/icons.js